import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout1 from "../../DataLayouts/Layout1";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { fetchEmployeeLeaveBalanceReport } from "../../../Apis/Employee-api";
import { MAIN_URL } from "../../../Configurations/Urls";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import axios from "axios";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
  prev_page: null,
  next_page: null,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_person_leave_balance_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();
  searchParams.forEach((value, key) => {
    if (key.endsWith("_operator") || key.endsWith("_logic") || !key.startsWith("filter_")) return;
    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);
    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);
    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;
      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }
      filters.push({ field, operator, value: filterValue });
    }
  });
  return filters;
};

const DEFAULT_COLUMNS = [
  {
    field: "Employee_name",
    label: "Employee_name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "employee_code",
    label: "employee_code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "leave_type_name",
    label: "leave_type_name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "leave_period_start_date",
    label: "leave_period_start_date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "leave_period_end_date",
    label: "leave_period_end_date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "entitled_days",
    label: "entitled_days",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "carry_forward_days",
    label: "carry_forward_days",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "balance_days",
    label: "balance_days",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "encashed_days",
    label: "encashed_days",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "adjusted_days",
    label: "adjusted_days",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

function LevaeBalanceReportList() {
  const [leaves, setLeaves] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const { id } = useParams();

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Leave Balance Report_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Load data via POST /filters (metadata-driven) or fallback to GET
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;
    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_person_leave_balance_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/leave-balances/filters`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          data = result.data ?? [];
          if (result.success && result.pagination) {
            setPaginationData({
              page: result.pagination.page ?? 1,
              pageSize: result.pagination.pageSize || result.pagination.limit || 10,
              total: result.pagination.total || 0,
              totalPages: result.pagination.totalPages || 1,
              hasNext: result.pagination.hasNext || false,
              hasPrev: result.pagination.hasPrev || false,
              prev_page: result.pagination.prev_page ?? null,
              next_page: result.pagination.next_page ?? null,
            });
          } else if (!result.success) {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 503 || response.status === 404) {
          const fallback = await fetchEmployeeLeaveBalanceReport(org.organization_id);
          data = fallback?.data || [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
        setLeaves(
          data.map((item) => {
            const name = item?.Employee_name ?? item?.full_name ?? item?.fullname ?? "";
            return {
              ...item,
              id: item?.organization_person_leave_balance_id || "",
              employee_code: item?.employee_code ?? "-",
              Employee_name: item?.Employee_name,
              // fullname: name,
              leave_type_name: item?.leave_type_name ?? "-",
              entitled_days: item?.entitled_days ?? "",
              leave_period_end_date: item?.leave_period_end_date ?? "",
              leave_period_start_date: item?.leave_period_start_date ?? "",
              leave_taken_days: item?.leave_taken_days ?? "",
              balance_days: item?.balance_days ?? "",
              adjusted_days: item?.adjusted_days ?? "",
              carry_forward_days: item?.carry_forward_days ?? "",
            };
          })
        );
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching employee leave balance:", err);
        toast.error("Failed to load employee leave balance report");
        setLeaves([]);
        setPaginationData(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    return () => fetchControllerRef.current?.abort();
  }, [org, searchParams]);

  let deleteemployeeleave = async (id) => {
    try {
      const org_id = org.organization_id;
      const response = await axios.delete(
        `${MAIN_URL}/api/organizations/${org_id}/employee-entitlements/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Delete failed:", error);
      toast.error(
        error.response?.data?.error ||
          "Failed to delete Employee Leave Entitlement"
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/leave-balances/taken/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      console.log("Successfully deleted units-types with id:", id);
      return Promise.resolve();
    } catch (error) {
      console.error("Delete failed:", error);
      return Promise.reject(error);
    }
  };

  const handleEdit = useCallback(
    (item) => {
      navigate(`/organization/employee/employee-exits/edit/${item.id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    () => {
      setSearchParams((prev) => new URLSearchParams(prev));
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Employee Leave Balance Report"}
        btnName={null}
        Data={leaves}
        tableHeaders={[
          {
            name: "Employee Name",
            value_key: "Employee_name",
            textStyle: "capitalize",
          },
          { name: "Leave Type", value_key: "leave_type_name" },
          { name: "Leave Start Date", value_key: "leave_period_start_date" },
          { name: "Leave End Date", value_key: "leave_period_end_date" },
          { name: "Leave Taken Days", value_key: "leave_taken_days" },
          { name: "Balance Days", value_key: "balance_days" },
          {
            name: "Entitle days",
            value_key: "entitled_days",
            textStyle: "capitalize",
          },
          { name: "Carry Forward Days", value_key: "carry_forward_days" },
          { name: "Adjusted Days", value_key: "adjusted_days" },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Employee Leave Balance Report",
          "Employee Leave Balance Report",
          "Add Employee Balance Report",
          "Employee Leaves Balance Report",
        ]}
        Route={"employee-leave-balance-report"}
        setData={setLeaves}
        DeleteFunc={deleteemployeeleave}
      />

      <TableDataGeneric
        tableName="employee_leave_balances"
        primaryKey="organization_person_leave_balance_id"
        heading="Employee Leave Balance Report"
        data={leaves}
        sortname="organization_person_leave_balance_id"
        showActions={false}
        Route="/employee-leave-balance-report"
        DeleteFunc={handleDelete}
        EditFunc={handleEdit}
        onSortChange={handleSortChange}
        onFilterChange={handleFilterChange}
        token={localStorage.getItem("token")}
        configss={configColumns}
        paginationData={paginationData}
        filterModule="employee_leave_balances"
        {...(tableConfig && { config: tableConfig })}
      />
    </>
  );
}

export default LevaeBalanceReportList;
